import { Button, DraggableWindowHeader } from "@hoylu/client-common";
import Cleave from "cleave.js/react";
import { clsx } from "clsx";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import {
  selectDashboardWorkspaceDetails,
  selectWorkspaces,
} from "../../state/workspaces/workspaces.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import { Modal } from "./Modal";
import Styles from "./OpenSharedWorkspaceDialog.module.css";

const WORKSPACE_ID_LENGTH = 9;

export function rawValue(value: string) {
  let result = "";

  for (let i = 0; i < value.length; i++) {
    if (Number.isNaN(Number.parseInt(value.charAt(i), 10))) continue;
    result += value.charAt(i);
  }

  return result.slice(0, WORKSPACE_ID_LENGTH);
}

export const OpenSharedWorkspaceDialog = () => {
  const dispatch = useDispatch();

  const t = useI18n("SHARED_WORKSPACE.");
  const tClose = useI18n("CLOSE_BUTTON.");

  const workspaceDetails = useSelector(selectDashboardWorkspaceDetails);
  const workspaces = useSelector(selectWorkspaces);

  const [searchTerm, setSearchTerm] = React.useState("");

  const handleClose = () => {
    dispatch(workspacesActions.cancelDashboardOption());
  };

  const handleOpen: React.SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (workspaceDetails[searchTerm]) {
      const workspace = workspaces.find((ws) => ws.workspaceId === searchTerm);

      if (workspace?.templateId) {
        // This workspace is a template, create a new workspace from its ID.
        dispatch(
          workspacesActions.createWorkspaceFromTemplateId.request({
            templateId: workspace.templateId,
          }),
        );
      } else {
        // We know about this workspace, open it.
        dispatch(workspacesActions.editWorkspace({ workspaceId: searchTerm }));
      }
    } else {
      // We don't know about this workspace, enqueue loading it.
      dispatch(
        workspacesActions.queueEditWorkspace({
          workspaceId: searchTerm,
        }),
      );
    }
    handleClose();
  };

  return (
    <Modal handleOutsideClick={handleClose}>
      <div className={Styles.openSharedWorkspaceDialog}>
        <DraggableWindowHeader
          close={tClose("CLOSE")}
          onClose={handleClose}
          title={t("OPEN_SHARED_WORKSPACE")}
        />
        <div className={Styles.openWorkspace}>
          <div className={clsx("hoylu-ui-icons-supply-chain", Styles.icon)} />
          {t("ENTER_ID")}
        </div>
        <form className={Styles.form} onSubmit={handleOpen}>
          <Cleave
            autoFocus
            className={Styles.cleave}
            onChange={(e: any) => {
              const newValue = rawValue(e.target.value);
              if (newValue !== searchTerm) {
                setSearchTerm(newValue);
              }
            }}
            options={{
              delimiters: ["-", "-"],
              blocks: [3, 3, 3],
              numericOnly: true,
            }}
            type="text"
            // Cleave needs some sort of initial value for it's slice function when pasting on mobile
            // This initial value should never appear since it's set to numericOnly
            value="ID"
          />
          <Button
            data-test-id="open-workspace-button"
            disabled={searchTerm.length !== WORKSPACE_ID_LENGTH}
            formType="submit"
            toolTip={t("OPEN")}
            type="primary"
          >
            {t("OPEN")}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default OpenSharedWorkspaceDialog;
